import React, { useEffect, useMemo, useState } from 'react';

import { TextButton } from '@hcs/design-system';
import { Dropdown, DropdownOptionType } from '@hcs/design-system';
import { MultiSelect, Option } from '@hcs/design-system';
import { Dialog } from '@hcs/design-system';
import { MenuItem } from '@hcs/design-system';
import { HomePriceIcon } from '@hcs/design-system';
import { useActiveState } from '@hcs/hooks';
import { usePatchReportPreferencesForOrg } from '@hcs/huell';
import { useReportPreferencesForOrg } from '@hcs/huell';
import { SelectedValuationMethod } from '@hcs/types';
import { ValueTypes } from '@hcs/types';
import { IncludeHcAdjustments, ValuationMethod } from '@hcs/types';
import { formatList, formatValuationMethod } from '@hcs/utils';

import styles from './OrgReportPreferences.module.css';

type AdjustmentId = 'Property Details' | 'Date';
const DEFAULT_ADJUSTMENTS: IncludeHcAdjustments = {
  hcAdjustmentDate: true,
  hcAdjustmentPropertyDetails: true,
};
const getOptions: (
  valueType: ValueTypes
) => DropdownOptionType<ValuationMethod>[] = (valueType) => [
  {
    label: formatValuationMethod(ValuationMethod.Avm, valueType),
    value: ValuationMethod.Avm,
  },
  {
    label: formatValuationMethod(ValuationMethod.Comps, valueType),
    value: ValuationMethod.Comps,
  },
  {
    label: formatValuationMethod(ValuationMethod.UserEntered, valueType),
    value: ValuationMethod.UserEntered,
  },
];
const getAdjustmentOptions: (
  valueType: ValueTypes
) => Option<AdjustmentId>[] = (valueType) => [
  {
    label: `${
      valueType === 'RENTAL' ? 'RPI Adjustment' : 'HPI Adjustment'
    } - A time-based adjustment to match the report effective date`,
    value: 'Date',
  },
  {
    label:
      'HouseCanary Adjustment - Attribute-based adjustment to match the subject property',
    value: 'Property Details',
  },
];
const dataHcName = 'default-valuation-method-control';
export const DefaultValuationMethodControl = ({
  valueType,
}: {
  valueType: ValueTypes;
}) => {
  const isRental = valueType === 'RENTAL';
  const { active, handleOpen, handleClose } = useActiveState();
  const { isFetched, data: orgReportPrefs } = useReportPreferencesForOrg();
  const { mutate: patchOrgPrefs } = usePatchReportPreferencesForOrg();
  const currentPreferenceValue = isRental
    ? orgReportPrefs?.preferences?.pexpDefaultSelectedValueRental
    : orgReportPrefs?.preferences?.pexpDefaultSelectedValue;
  const [newIncludedAdjustments, setNewIncludedAdjustments] =
    useState<IncludeHcAdjustments>(
      currentPreferenceValue?.type === ValuationMethod.Comps
        ? currentPreferenceValue.hcAdjustments
        : DEFAULT_ADJUSTMENTS
    );
  const [newValMethod, setNewValMethod] = useState<ValuationMethod | undefined>(
    currentPreferenceValue?.type || ValuationMethod.Avm
  );
  useEffect(() => {
    if (
      currentPreferenceValue?.type
        ? currentPreferenceValue.type !== newValMethod
        : newValMethod !== ValuationMethod.Avm
    ) {
      setNewValMethod(currentPreferenceValue?.type || ValuationMethod.Avm);
    }
    if (
      currentPreferenceValue?.type === ValuationMethod.Comps &&
      (currentPreferenceValue.hcAdjustments.hcAdjustmentDate !==
        newIncludedAdjustments.hcAdjustmentDate ||
        currentPreferenceValue.hcAdjustments.hcAdjustmentPropertyDetails !==
          newIncludedAdjustments.hcAdjustmentPropertyDetails)
    ) {
      setNewIncludedAdjustments(currentPreferenceValue.hcAdjustments);
    }
  }, [currentPreferenceValue]);

  const handleCancel = () => {
    handleClose();
    setNewValMethod(currentPreferenceValue?.type);
    if (currentPreferenceValue?.type === ValuationMethod.Comps) {
      setNewIncludedAdjustments(currentPreferenceValue.hcAdjustments);
    } else {
      setNewIncludedAdjustments(DEFAULT_ADJUSTMENTS);
    }
  };
  const multiSelectValue = useMemo(() => {
    const value: AdjustmentId[] = [];
    if (newIncludedAdjustments?.hcAdjustmentDate) {
      value.push('Date');
    }
    if (newIncludedAdjustments?.hcAdjustmentPropertyDetails) {
      value.push('Property Details');
    }
    return value;
  }, [newIncludedAdjustments]);
  const label = isRental
    ? 'Rental Report Default Selected Value'
    : 'Value Report Default Selected Value';
  const description = `This setting effects the selected value on newly created ${
    isRental ? 'rental' : 'value'
  } reports. Change this setting if you prefer to user the${
    isRental ? ' Rental' : ''
  } Comparable Value as the selected value on ${
    isRental ? 'rental' : 'value'
  } reports.`;
  return (
    <>
      <MenuItem
        dataHcName={dataHcName}
        Icon={HomePriceIcon}
        label={label}
        description={description}
        contentRight={
          isFetched && (
            <div className={styles.Switch}>
              <div
                data-hc-name={`${dataHcName}-current-value`}
                className={styles.SelectedValue}
              >
                {formatValuationMethod(
                  currentPreferenceValue?.type || ValuationMethod.Avm,
                  valueType
                )}
                {currentPreferenceValue?.type === ValuationMethod.Comps ? (
                  <div
                    data-hc-name={`${dataHcName}-comp-adjustments`}
                    className={styles.CompAdjustments}
                  >
                    {multiSelectValue.length
                      ? `Adjust property values based on ${formatList(
                          multiSelectValue
                        )}`
                      : 'No adjustments'}
                  </div>
                ) : null}
              </div>

              <div className={styles.SwitchLabel}>
                <TextButton
                  dataHcName={`${dataHcName}-edit`}
                  onClick={handleOpen}
                  label="Change"
                />
              </div>
            </div>
          )
        }
      />
      <Dialog
        dataHcName={`${dataHcName}-dialog`}
        active={active}
        onClose={handleCancel}
        title={`Change ${label}`}
        subtitle={description}
        actions={[
          {
            dataHcName: `${dataHcName}-cancel`,
            label: 'Cancel',
            secondary: true,
            onClick: handleCancel,
          },
          {
            dataHcName: `${dataHcName}-save`,
            label: 'Save',
            disabled:
              (newValMethod === ValuationMethod.Comps &&
                currentPreferenceValue?.type === ValuationMethod.Comps &&
                newIncludedAdjustments.hcAdjustmentPropertyDetails ===
                  currentPreferenceValue.hcAdjustments
                    .hcAdjustmentPropertyDetails &&
                newIncludedAdjustments.hcAdjustmentDate ===
                  currentPreferenceValue.hcAdjustments.hcAdjustmentDate) ||
              (newValMethod !== ValuationMethod.Comps &&
                newValMethod === currentPreferenceValue?.type) ||
              (newValMethod === ValuationMethod.Avm &&
                !currentPreferenceValue?.type),
            onClick: () => {
              const path = `/${
                isRental
                  ? 'pexpDefaultSelectedValueRental'
                  : 'pexpDefaultSelectedValue'
              }`;
              if (newValMethod && newValMethod !== ValuationMethod.Avm) {
                const value: SelectedValuationMethod =
                  newValMethod === ValuationMethod.Comps
                    ? {
                        type: newValMethod,
                        hcAdjustments: newIncludedAdjustments,
                      }
                    : {
                        type: newValMethod || ValuationMethod.Avm,
                      };

                patchOrgPrefs([
                  {
                    op: 'add',
                    path,
                    value,
                  },
                ]);
              } else {
                patchOrgPrefs([
                  {
                    op: 'remove',
                    path,
                  },
                ]);
              }
              handleClose();
            },
          },
        ]}
      >
        <div style={{ height: '250px' }}>
          <Dropdown
            dataHcName={`${dataHcName}-val-method-dropdown`}
            value={newValMethod}
            options={getOptions(valueType)}
            onSelect={setNewValMethod}
          />
          {newValMethod === ValuationMethod.Comps && (
            <MultiSelect
              dataHcName={`${dataHcName}-adjustments-select`}
              value={multiSelectValue}
              options={getAdjustmentOptions(valueType)}
              onChange={(newValues) => {
                const value: IncludeHcAdjustments = {
                  hcAdjustmentDate: false,
                  hcAdjustmentPropertyDetails: false,
                };
                newValues.forEach((v) => {
                  if (v === 'Date') {
                    value.hcAdjustmentDate = true;
                  }
                  if (v === 'Property Details') {
                    value.hcAdjustmentPropertyDetails = true;
                  }
                });
                setNewIncludedAdjustments(value);
              }}
            />
          )}
        </div>
      </Dialog>
    </>
  );
};
